
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import styles from './Notification.module.scss';

// Тип оповещения
export type AlertType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
    id: string;
    message: string;
    type: AlertType;
    autoHideDuration?: number;
    translateParams?: Record<string, any>;
}

interface NotificationContextType {
    notifications: Notification[];
    showNotification: (message: string, type?: AlertType, autoHideDuration?: number, translateParams?: Record<string, any>) => void;
    showError: (message: string, translateParams?: Record<string, any>) => void;
    showSuccess: (message: string, translateParams?: Record<string, any>) => void;
    showWarning: (message: string, translateParams?: Record<string, any>) => void;
    showInfo: (message: string, translateParams?: Record<string, any>) => void;
    closeNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType>({
    notifications: [],
    showNotification: () => { },
    showError: () => { },
    showSuccess: () => { },
    showWarning: () => { },
    showInfo: () => { },
    closeNotification: () => { },
});

export const useNotification = () => useContext(NotificationContext);

const Alert: React.FC<{
    type: AlertType;
    onClose: () => void;
    children: React.ReactNode;
}> = ({ type, onClose, children }) => {
    return (
        <div className={`${styles.alert} ${styles[type]}`}>
            <div className={styles.alertContent}>{children}</div>
            <button className={styles.alertCloseButton} onClick={onClose}>
                ×
            </button>
        </div>
    );
};

const Snackbar: React.FC<{
    open: boolean;
    autoHideDuration?: number;
    onClose: () => void;
    children: React.ReactNode;
}> = ({ open, autoHideDuration, onClose, children }) => {
    useEffect(() => {
        if (open && autoHideDuration) {
            const timer = setTimeout(() => {
                onClose();
            }, autoHideDuration);
            return () => clearTimeout(timer);
        }
    }, [open, autoHideDuration, onClose]);

    return (
        <div className={styles.snackbar}>
            {children}
        </div>
    );
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const { t } = useTranslation();

    const showNotification = useCallback(
        (
            message: string,
            type: AlertType = 'info',
            autoHideDuration = 5000,
            translateParams?: Record<string, any>
        ) => {
            const id = uuidv4();
            setNotifications(prev => [...prev, { id, message, type, autoHideDuration, translateParams }]);
        },
        []
    );

    const showError = useCallback(
        (message: string, translateParams?: Record<string, any>) => {
            showNotification(message, 'error', 6000, translateParams);
        },
        [showNotification]
    );

    const showSuccess = useCallback(
        (message: string, translateParams?: Record<string, any>) => {
            showNotification(message, 'success', 4000, translateParams);
        },
        [showNotification]
    );

    const showWarning = useCallback(
        (message: string, translateParams?: Record<string, any>) => {
            showNotification(message, 'warning', 5000, translateParams);
        },
        [showNotification]
    );

    const showInfo = useCallback(
        (message: string, translateParams?: Record<string, any>) => {
            showNotification(message, 'info', 4000, translateParams);
        },
        [showNotification]
    );

    const closeNotification = useCallback((id: string) => {
        setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, []);

    const value = {
        notifications,
        showNotification,
        showError,
        showSuccess,
        showWarning,
        showInfo,
        closeNotification,
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
            {notifications.map(notification => (
                <Snackbar
                    key={notification.id}
                    open={true}
                    autoHideDuration={notification.autoHideDuration}
                    onClose={() => closeNotification(notification.id)}
                >
                    <Alert
                        type={notification.type}
                        onClose={() => closeNotification(notification.id)}
                    >
                        {notification.translateParams
                            ? t(notification.message, notification.translateParams)
                            : t(notification.message)}
                    </Alert>
                </Snackbar>
            ))}
        </NotificationContext.Provider>
    );
};

export default NotificationProvider; 